/* ============================================================
   THEME.JS — progressive enhancement for the Pluck theme.

   Every behaviour here enhances working HTML: product/cart forms
   post normally, the header cart link goes to /cart, filters and
   sort submit as a GET form, search links to /search, and
   load-more / swatch / pagination controls are plain links.

   Custom elements (spec §1.3):
     announcement-rotator, sticky-header, menu-drawer,
     predictive-search, cart-drawer, cart-items, product-info,
     quantity-input, variant-picker, bundle-picker, media-gallery, sticky-buy-bar,
     facet-filters, product-recommendations, featured-slider

   Prices shown by JS are either pre-formatted by Shopify's `money`
   filter in Liquid, or formatted with the store's own money format
   (window.theme.moneyFormat) — no currency is hard-coded here.
============================================================= */

const theme = window.theme || {
  cartType: 'page',
  moneyFormat: '{{amount}}',
  routes: {
    root_url: '/', cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change',
    cart_update_url: '/cart/update', search_url: '/search', predictive_search_url: '/search/suggest',
    product_recommendations_url: '/recommendations/products'
  },
  strings: {}
};
const strings = Object.assign({
  addToCart: 'Add to cart', addedToCart: 'Added to cart', soldOut: 'Sold out',
  unavailable: 'Unavailable', cartError: 'Something went wrong. Please try again.', searchLoading: 'Loading…'
}, theme.strings || {});

const SUCCESS_MS = 2200;
const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

/* ==========================================================
   Helpers
========================================================== */

function jsonEndpoint(url){
  return url.replace(/\/$/, '') + '.js';
}

function debounce(fn, wait){
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), wait); };
}

function parseHTML(html){
  return new DOMParser().parseFromString(html, 'text/html');
}

/* Store money format (e.g. "£{{amount}}", "{{amount_with_comma_separator}} €"). */
function formatMoney(cents, format){
  const value = Number(cents) || 0;
  const fmt = format || theme.moneyFormat || '{{amount}}';
  const withDelimiters = (number, precision, thousands, decimal) => {
    const fixed = (number / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const wholeWithSep = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? wholeWithSep + decimal + fraction : wholeWithSep;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount': return withDelimiters(value, 2, ',', '.');
      case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', '');
      case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return withDelimiters(value, 2, ' ', '.');
      default: return withDelimiters(value, 2, ',', '.');
    }
  });
}

/* POST to a Shopify AJAX cart endpoint. Rejects with Shopify's own message on errors. */
async function cartRequest(url, body){
  const isForm = body instanceof FormData;
  const res = await fetch(jsonEndpoint(url), {
    method: 'POST',
    headers: Object.assign(
      { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      isForm ? {} : { 'Content-Type': 'application/json' }
    ),
    body: isForm ? body : JSON.stringify(body)
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok || (data && data.status && Number(data.status) >= 400)) {
    const message = data && typeof data.description === 'string' && data.description
      ? data.description
      : (data && typeof data.message === 'string' && data.message) || strings.cartError;
    throw new Error(message);
  }
  return data;
}

function showFormError(container, message){
  if (!container) return;
  const text = container.querySelector('[data-form-error-text], [data-line-error-text]');
  if (text) text.textContent = message;
  container.hidden = false;
}

function hideFormError(container){
  if (container) container.hidden = true;
}

/* ---- Focus trap + scroll lock shared by drawers, sheets and the search overlay ---- */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function trapFocus(container, event){
  if (event.key !== 'Tab') return;
  const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null || el === document.activeElement);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}

let scrollLocks = 0;
function lockScroll(){ scrollLocks++; document.documentElement.classList.add('scroll-locked'); }
function unlockScroll(){ scrollLocks = Math.max(0, scrollLocks - 1); if (!scrollLocks) document.documentElement.classList.remove('scroll-locked'); }

/* ==========================================================
   Cart state rendering (Section Rendering API)
========================================================== */

function sectionsUrl(){
  return window.location.pathname;
}

function onCartPage(){
  return window.location.pathname.replace(/\/$/, '') === theme.routes.cart_url.replace(/\/$/, '');
}

function drawerMode(){
  return theme.cartType === 'drawer' && Boolean(document.querySelector('cart-drawer')) && !onCartPage();
}

/* Swap the cart drawer contents and header bubble from a `sections` payload. */
function renderCartSections(sections){
  if (!sections) return;
  if (sections['cart-drawer']) {
    const doc = parseHTML(sections['cart-drawer']);
    const next = doc.querySelector('#CartDrawer-Contents');
    const current = document.querySelector('#CartDrawer-Contents');
    if (next && current) current.innerHTML = next.innerHTML;
  }
  if (sections['cart-icon-bubble']) {
    const doc = parseHTML(sections['cart-icon-bubble']);
    const next = doc.querySelector('[data-cart-bubble]');
    const current = document.querySelector('#cart-icon-bubble [data-cart-bubble]');
    if (next && current) current.replaceWith(next);
  }
  updateFreeShipping(document);
}

async function refreshCartSections(){
  try {
    const res = await fetch(`${sectionsUrl()}?sections=${CART_SECTIONS.join(',')}`);
    if (res.ok) renderCartSections(await res.json());
  } catch (e) { /* ignored — next page load shows the right state */ }
}

/* Free-shipping bar: convert the threshold when shopping in a non-default currency. */
function updateFreeShipping(root){
  root.querySelectorAll('[data-free-shipping]').forEach(el => {
    if (el.dataset.currencyMatch === 'true') return;
    const rate = window.Shopify && Shopify.currency && Number(Shopify.currency.rate) || 1;
    const threshold = Math.round(Number(el.dataset.threshold) * rate);
    const total = Number(el.dataset.total) || 0;
    const remaining = threshold - total;
    const text = el.querySelector('[data-free-shipping-text]');
    const fill = el.querySelector('[data-free-shipping-fill]');
    const bar = el.querySelector('[role="progressbar"]');
    const percent = Math.min(100, Math.round((total / threshold) * 100)) || 0;
    if (text) {
      text.innerHTML = remaining > 0
        ? el.dataset.textRemaining.replace('[amount]', formatMoney(remaining))
        : el.dataset.textReached;
    }
    if (fill) fill.style.width = `${percent}%`;
    if (bar) bar.setAttribute('aria-valuenow', percent);
  });
}

/* Shared "add to cart" for the product form, sticky bar, product-card quick add and upsell. */
function addToCart(form, options = {}){
  const body = new FormData(form);
  /* A named submit button (e.g. a "Complete the set" tile: name="id" value=variant) sends its value, as a native submit would. */
  const submitter = options.submitter;
  if (submitter && submitter.name) body.set(submitter.name, submitter.value);
  return submitCartAdd(body, options);
}

/* Several real variants in one request: items = [{ id, quantity }, …] (pack picker). */
function addItemsToCart(items, options){
  return submitCartAdd({ items }, options);
}

async function submitCartAdd(body, { buttons = [], errorBox = null, toast = null, opener = null } = {}){
  if (buttons.some(btn => btn.getAttribute('aria-busy') === 'true')) return;
  hideFormError(errorBox);
  setButtonsLoading(buttons, true);

  const useDrawer = drawerMode();
  if (useDrawer) {
    if (body instanceof FormData) {
      body.append('sections', CART_SECTIONS.join(','));
      body.append('sections_url', sectionsUrl());
    } else {
      body.sections = CART_SECTIONS.join(',');
      body.sections_url = sectionsUrl();
    }
  }

  try {
    const data = await cartRequest(theme.routes.cart_add_url, body);
    setButtonsLoading(buttons, false);
    if (onCartPage()) { window.location.reload(); return; }
    if (useDrawer && data && data.sections) {
      renderCartSections(data.sections);
      document.querySelector('cart-drawer').open(opener || buttons[0] || null);
    } else {
      refreshCartSections();
      showSuccess(buttons, toast);
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  } catch (error) {
    setButtonsLoading(buttons, false);
    showFormError(errorBox, error.message || strings.cartError);
  }
}

function setButtonsLoading(buttons, loading){
  buttons.forEach(btn => {
    if (loading) {
      btn.style.width = `${btn.offsetWidth}px`;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    } else {
      btn.style.width = '';
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  });
}

function showSuccess(buttons, toast){
  buttons.forEach(btn => {
    btn.classList.add('is-success');
    const text = btn.querySelector('[data-add-label]');
    if (text) { btn.dataset.label = text.textContent; text.textContent = strings.addedToCart; }
    const icon = btn.querySelector('[data-add-icon]');
    const check = btn.querySelector('[data-add-check]');
    if (icon) icon.hidden = true;
    if (check) check.hidden = false;
  });
  if (toast) toast.hidden = false;
  setTimeout(() => {
    buttons.forEach(btn => {
      btn.classList.remove('is-success');
      const text = btn.querySelector('[data-add-label]');
      if (text && btn.dataset.label && !btn.disabled) text.textContent = btn.dataset.label;
      const icon = btn.querySelector('[data-add-icon]');
      const check = btn.querySelector('[data-add-check]');
      if (icon) icon.hidden = false;
      if (check) check.hidden = true;
    });
    if (toast) toast.hidden = true;
  }, SUCCESS_MS);
}

/* Quick add (product cards) and "Complete the set" — delegated so re-rendered forms keep working. */
document.addEventListener('submit', event => {
  const form = event.target.closest('.quick-add-form');
  if (!form) return;
  event.preventDefault();
  const submitter = event.submitter && event.submitter.matches('[data-add-button]') ? event.submitter : null;
  const buttons = submitter ? [submitter] : Array.from(form.querySelectorAll('[data-add-button]'));
  addToCart(form, { buttons, errorBox: form.querySelector('[data-form-error]'), opener: submitter, submitter });
});

/* ==========================================================
   <quantity-input>
========================================================== */
class QuantityInput extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.input = this.querySelector('[data-qty-input]');
    this.minusBtn = this.querySelector('[data-qty-minus]');
    this.plusBtn = this.querySelector('[data-qty-plus]');
    if (!this.input) return;
    this.input.dataset.last = this.input.value;
    if (this.minusBtn) this.minusBtn.addEventListener('click', () => this.stepBy(-1));
    if (this.plusBtn) this.plusBtn.addEventListener('click', () => this.stepBy(1));
    this.input.addEventListener('change', () => { this.clamp(); this.sync(); this.emit(); });
    this.sync();
  }
  get min(){ return parseInt(this.input.min || '0', 10); }
  get max(){ return this.input.max ? parseInt(this.input.max, 10) : Infinity; }
  get step(){ return parseInt(this.input.step || '1', 10) || 1; }
  get value(){ return parseInt(this.input.value, 10); }
  stepBy(direction){
    const current = isNaN(this.value) ? this.min : this.value;
    this.input.value = Math.min(this.max, Math.max(this.min, current + direction * this.step));
    this.sync();
    this.emit();
  }
  clamp(){
    let v = this.value;
    if (isNaN(v)) v = this.min;
    this.input.value = Math.min(this.max, Math.max(this.min, v));
  }
  sync(){
    const v = this.value;
    if (this.minusBtn) this.minusBtn.disabled = v <= this.min;
    if (this.plusBtn) this.plusBtn.disabled = v >= this.max;
  }
  setRule({ min, max, step }){
    this.input.min = min || 1;
    this.input.step = step || 1;
    if (max) this.input.max = max; else this.input.removeAttribute('max');
    this.clamp();
    this.sync();
  }
  revert(){
    this.input.value = this.input.dataset.last;
    this.sync();
  }
  emit(){
    this.dispatchEvent(new CustomEvent('quantity-change', { bubbles: true, detail: { value: this.value } }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ==========================================================
   <cart-items> — drawer and cart page lines
   Drawer: /cart/change.js + Section Rendering, focus kept on the control.
   Page: /cart/change.js then reload (the page is fully server-rendered).
========================================================== */
class CartItems extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.timers = {};
    this.addEventListener('quantity-change', event => {
      /* The line element, not the <quantity-input> (which carries the same data-line-key). */
      const line = event.target.closest('.cart-line') || event.target.closest('[data-line-key]');
      if (!line) return;
      const key = line.dataset.lineKey;
      const control = event.target.closest('quantity-input');
      clearTimeout(this.timers[key]);
      this.timers[key] = setTimeout(() => this.update(line, event.detail.value, control), 350);
    });
    this.addEventListener('click', event => {
      const remove = event.target.closest('[data-remove-line]');
      if (!remove) return;
      event.preventDefault();
      this.update(remove.closest('[data-line-key]'), 0, remove);
    });
  }

  async update(line, quantity, control){
    if (!line || isNaN(quantity)) return;
    const key = line.dataset.lineKey;
    const errorBox = line.querySelector('[data-line-error]');
    const inDrawer = this.dataset.context === 'drawer';
    const activeSelector = control && control.matches('[data-remove-line]') ? null
      : (document.activeElement && document.activeElement.matches('[data-qty-minus], [data-qty-plus], [data-qty-input]')
        ? `[data-line-key="${key}"] [${document.activeElement.hasAttribute('data-qty-minus') ? 'data-qty-minus' : document.activeElement.hasAttribute('data-qty-plus') ? 'data-qty-plus' : 'data-qty-input'}]`
        : null);

    hideFormError(errorBox);
    line.classList.add('is-loading');
    const body = { id: key, quantity };
    if (inDrawer) { body.sections = CART_SECTIONS; body.sections_url = sectionsUrl(); }

    try {
      const data = await cartRequest(theme.routes.cart_change_url, body);
      if (!inDrawer) { window.location.reload(); return; }
      renderCartSections(data.sections);
      /* Shopify caps quantities at available stock without an error — tell the customer. */
      const item = (data.items || []).find(i => i.key === key);
      if (item && item.quantity < quantity) {
        const newLine = document.querySelector(`#CartDrawer [data-line-key="${key}"] [data-line-error]`);
        showFormError(newLine, strings.quantityLimited || strings.cartError);
      }
      const target = activeSelector && document.querySelector(`#CartDrawer ${activeSelector}`);
      if (target) target.focus();
      else { const panel = document.querySelector('#CartDrawer .drawer__panel'); if (panel) panel.focus(); }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (error) {
      line.classList.remove('is-loading');
      const qty = line.querySelector('quantity-input');
      if (qty && qty.revert) qty.revert();
      showFormError(errorBox, error.message || strings.cartError);
    }
  }
}
customElements.define('cart-items', CartItems);

/* Gift note — saved as the customer types (the textarea also posts with the cart form). */
document.addEventListener('input', debounce(event => {
  const note = event.target.closest && event.target.closest('[data-cart-note]');
  if (!note) return;
  cartRequest(theme.routes.cart_update_url, { note: note.value }).catch(() => {});
}, 400));

/* ==========================================================
   <cart-drawer>
========================================================== */
class CartDrawer extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.panel = this.querySelector('.drawer__panel');
    this.addEventListener('click', event => { if (event.target.closest('[data-drawer-close]')) this.close(); });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.close();
      else trapFocus(this.panel, event);
    });
    updateFreeShipping(this);
  }
  open(opener){
    if (this.classList.contains('is-open')) return;
    this.opener = opener || document.activeElement;
    this.classList.add('is-open');
    lockScroll();
    requestAnimationFrame(() => this.panel.focus());
  }
  close(){
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    unlockScroll();
    if (this.opener && document.contains(this.opener)) this.opener.focus();
  }
}
customElements.define('cart-drawer', CartDrawer);

document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-cart-open]');
  if (!trigger || !drawerMode()) return;
  event.preventDefault();
  document.querySelector('cart-drawer').open(trigger);
});

/* ==========================================================
   <announcement-rotator>
========================================================== */
class AnnouncementRotator extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    if (this.slides.length < 2) return;
    this.index = 0;
    this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    const prev = this.querySelector('[data-announcement-prev]');
    const next = this.querySelector('[data-announcement-next]');
    if (prev) prev.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
    if (next) next.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
    ['mouseenter', 'focusin'].forEach(ev => this.addEventListener(ev, () => this.stop()));
    ['mouseleave', 'focusout'].forEach(ev => this.addEventListener(ev, () => this.start()));
    this.start();
  }
  disconnectedCallback(){ this.stop(); }
  go(i){
    this.slides[this.index].hidden = true;
    this.index = (i + this.slides.length) % this.slides.length;
    this.slides[this.index].hidden = false;
  }
  start(){
    this.stop();
    if (this.dataset.autoplay !== 'true' || prefersReducedMotion()) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.speed);
  }
  stop(){ clearInterval(this.timer); }
  restart(){ this.start(); }
}
customElements.define('announcement-rotator', AnnouncementRotator);

/* ==========================================================
   <sticky-header> — condensed after 80px; "on-scroll-up" mode
========================================================== */
class StickyHeader extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.mode = this.dataset.sticky;
    this.lastY = window.scrollY;
    this.onScroll = () => {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 80);
      if (this.mode === 'on-scroll-up') {
        this.classList.toggle('is-hidden', y > this.lastY && y > 200 && !this.querySelector('details[open]'));
      }
      this.lastY = y;
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
    this.setHeight();
    window.addEventListener('resize', debounce(() => this.setHeight(), 150));
  }
  disconnectedCallback(){ window.removeEventListener('scroll', this.onScroll); }
  setHeight(){
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }
}
customElements.define('sticky-header', StickyHeader);

/* ==========================================================
   <menu-drawer> — mobile navigation
========================================================== */
class MenuDrawer extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.panel = this.querySelector('.drawer__panel');
    this.addEventListener('click', event => {
      if (event.target.closest('[data-drawer-close]')) this.close();
      if (event.target.closest('[data-search-open]')) this.close(false);
    });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.close();
      else trapFocus(this.panel, event);
    });
  }
  open(opener){
    this.opener = opener;
    this.classList.add('is-open');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    lockScroll();
    requestAnimationFrame(() => this.panel.focus());
  }
  close(returnFocus = true){
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    unlockScroll();
    if (this.opener) {
      this.opener.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.opener.focus();
    }
  }
}
customElements.define('menu-drawer', MenuDrawer);

document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-menu-drawer-open]');
  if (!trigger) return;
  const drawer = document.querySelector('menu-drawer');
  if (drawer) drawer.open(trigger);
});

/* ==========================================================
   <predictive-search> — header search overlay (spec §10.1)
   Debounced 250ms fetch of the predictive-search section, aborts
   in-flight requests, caches by term, keyboard listbox navigation.
========================================================== */
class PredictiveSearch extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.clearBtn = this.querySelector('[data-search-clear]');
    this.cache = new Map();
    this.header = this.closest('sticky-header');

    this.input.addEventListener('input', debounce(() => this.onInput(), 250));
    this.input.addEventListener('keydown', event => this.onKeydown(event));
    if (this.clearBtn) this.clearBtn.addEventListener('click', () => { this.input.value = ''; this.onInput(); this.input.focus(); });
    this.addEventListener('click', event => { if (event.target.closest('[data-search-close]')) this.close(); });
    this.addEventListener('keydown', event => {
      if (event.key === 'Tab' && this.classList.contains('is-open') && !isDesktop()) trapFocus(this, event);
    });
    this.addEventListener('focusout', event => {
      if (isDesktop() && this.classList.contains('is-open') && event.relatedTarget && !this.contains(event.relatedTarget)) this.close(false);
    });
  }

  open(opener){
    this.opener = opener;
    this.classList.add('is-open');
    if (this.header) this.header.classList.add('is-search-open');
    if (!isDesktop()) lockScroll();
    this.locked = !isDesktop();
    requestAnimationFrame(() => { this.input.focus(); this.input.select(); });
    if (this.input.value.trim().length >= 2) this.onInput();
  }

  close(returnFocus = true){
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    if (this.header) this.header.classList.remove('is-search-open');
    if (this.locked) { unlockScroll(); this.locked = false; }
    this.hideResults();
    if (returnFocus && this.opener && document.contains(this.opener)) this.opener.focus();
  }

  hideResults(){
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  async onInput(){
    const term = this.input.value.trim();
    if (this.clearBtn) this.clearBtn.hidden = term.length === 0;
    if (term.length < 2) { this.hideResults(); return; }

    if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.results.hidden = false;
    this.results.classList.add('is-loading');
    this.status.textContent = strings.searchLoading;

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,query,page',
      'resources[limit]': '4',
      'resources[options][unavailable_products]': 'last',
      section_id: this.dataset.section || 'predictive-search'
    });
    try {
      const res = await fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: this.controller.signal });
      if (!res.ok) throw new Error(res.status);
      const doc = parseHTML(await res.text());
      const markup = doc.querySelector('#predictive-search-results');
      const html = markup ? markup.outerHTML : '';
      this.cache.set(term, html);
      this.render(html);
    } catch (error) {
      if (error.name !== 'AbortError') { this.results.classList.remove('is-loading'); this.hideResults(); }
    }
  }

  render(html){
    this.results.classList.remove('is-loading');
    this.results.innerHTML = html;
    this.results.hidden = !html;
    this.input.setAttribute('aria-expanded', html ? 'true' : 'false');
    const wrapper = this.results.querySelector('[data-results-count]');
    const count = wrapper ? wrapper.dataset.resultsCount : 0;
    this.status.textContent = `${count}`;
    this.activeIndex = -1;
  }

  options(){
    return Array.from(this.results.querySelectorAll('[role="option"]'));
  }

  onKeydown(event){
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.input.value) { this.input.value = ''; this.onInput(); }
      else this.close();
      return;
    }
    const options = this.options();
    if (!options.length || this.results.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
      options.forEach((opt, i) => opt.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false'));
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.activeIndex > -1) {
      const link = options[this.activeIndex].querySelector('a');
      if (link) { event.preventDefault(); window.location.href = link.href; }
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-search-open]');
  if (!trigger) return;
  const search = document.querySelector('predictive-search');
  if (!search) return;
  event.preventDefault();
  search.open(trigger.closest('menu-drawer') ? document.querySelector('.header__search-toggle') : trigger);
});

/* ==========================================================
   <media-gallery>
========================================================== */
class MediaGallery extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.viewport = this.querySelector('[data-gallery-viewport]');
    if (!this.viewport) return;
    this.querySelectorAll('.pdp-gallery__thumb').forEach(btn => {
      btn.addEventListener('click', () => this.show(btn.dataset.mediaId, true));
    });
    this.viewport.addEventListener('scroll', debounce(() => {
      const index = Math.round(this.viewport.scrollLeft / (this.viewport.clientWidth || 1));
      const slide = this.viewport.children[index];
      if (slide && slide.dataset.mediaId) this.setActive(slide.dataset.mediaId);
    }, 80), { passive: true });
    if (this.dataset.initialMedia) this.show(this.dataset.initialMedia, false);
  }
  show(mediaId, smooth){
    if (!mediaId || !this.viewport) return;
    const slide = this.viewport.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    this.viewport.scrollTo({ left: slide.offsetLeft, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
    this.setActive(mediaId);
  }
  setActive(mediaId){
    if (this.activeId === mediaId) return;
    this.activeId = mediaId;
    this.querySelectorAll('.pdp-gallery__thumb').forEach(btn => {
      if (btn.dataset.mediaId === String(mediaId)) btn.setAttribute('aria-current', 'true');
      else btn.removeAttribute('aria-current');
    });
    this.querySelectorAll('.pdp-gallery__dot').forEach(dot => {
      dot.classList.toggle('is-active', dot.dataset.mediaId === String(mediaId));
    });
    this.viewport.querySelectorAll('video').forEach(video => {
      if (!video.closest(`[data-media-id="${mediaId}"]`)) video.pause();
    });
  }
}
customElements.define('media-gallery', MediaGallery);

/* ==========================================================
   <variant-picker>
========================================================== */
class VariantPicker extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.addEventListener('change', () => {
      /* Each fieldset carries its Shopify option position, so the selection array lines up
         with variant.options regardless of the order the fieldsets appear in. */
      const options = [];
      this.querySelectorAll('fieldset[data-option-position]').forEach(fieldset => {
        const checked = fieldset.querySelector('input:checked');
        const value = checked ? checked.value : null;
        options[Number(fieldset.dataset.optionPosition) - 1] = value;
        const legend = fieldset.querySelector('[data-selected-value]');
        if (legend && value !== null) legend.textContent = value;
      });
      this.dispatchEvent(new CustomEvent('variant:select', { bubbles: true, detail: { options } }));
    });
  }
}
customElements.define('variant-picker', VariantPicker);

/* ==========================================================
   <product-info> — product form + variant updates
========================================================== */
class ProductInfo extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    const json = this.querySelector('[data-product-json]');
    this.variants = json ? (JSON.parse(json.textContent).variants || []) : [];
    this.form = this.querySelector('form.product-form');
    this.errorBox = this.querySelector('.product-buy [data-form-error]');
    this.toast = this.querySelector('[data-cart-toast]');
    this.bundleState = null;
    if (this.form) {
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        const options = {
          buttons: Array.from(this.querySelectorAll('[data-add-button]')),
          errorBox: this.errorBox,
          toast: this.toast,
          opener: event.submitter || null
        };
        /* 2 / 3 pack: add every chosen variant in one /cart/add.js request. */
        if (this.inMixPack()) {
          if (this.bundleState.complete) addItemsToCart(this.bundleState.items, options);
          return;
        }
        const idInput = this.form.querySelector('[data-variant-id-input]');
        if (!idInput || !idInput.value || idInput.disabled) return;
        addToCart(this.form, options);
      });
    }
    this.addEventListener('variant:select', event => this.onVariantSelect(event.detail.options));
    this.addEventListener('bundle:change', event => this.onBundleChange(event.detail));
    this.addEventListener('bundle:preview', event => this.previewVariant(event.detail.variant));
  }

  inMixPack(){
    return Boolean(this.bundleState && this.bundleState.size > 1);
  }

  currentVariant(){
    const idInput = this.form && this.form.querySelector('[data-variant-id-input]');
    const id = idInput && !idInput.disabled ? Number(idInput.value) : null;
    return this.variants.find(v => v.id === id) || null;
  }

  onVariantSelect(options){
    const variant = this.variants.find(v => v.options.every((value, i) => value === options[i]));
    const idInput = this.form && this.form.querySelector('[data-variant-id-input]');
    if (idInput) { idInput.value = variant ? variant.id : ''; idInput.disabled = !variant; }
    hideFormError(this.errorBox);
    const bundle = this.querySelector('bundle-picker');
    if (bundle && bundle.refresh) bundle.refresh();
    if (this.inMixPack()) return;
    this.applyVariant(variant, true);
  }

  /* Price, availability, sticky bar, quantity rules, gallery and URL for one variant (1 pack). */
  applyVariant(variant, moveGallery){
    this.setButtonsAvailability(variant);
    const dynamic = this.querySelector('[data-dynamic-checkout]');
    if (dynamic) dynamic.hidden = !(variant && variant.available);
    if (!variant) return;

    this.updatePrice(variant);
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = variant.price;
    this.setStickyImage(variant);
    const quantity = this.querySelector('.product-buy quantity-input');
    if (quantity && quantity.setRule) quantity.setRule({ min: variant.quantity_min, max: variant.quantity_max, step: variant.quantity_step });
    if (moveGallery) this.previewVariant(variant);
    if (this.dataset.productUrl) window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
  }

  previewVariant(variant){
    if (!variant) return;
    const gallery = this.querySelector('media-gallery');
    if (gallery && variant.featured_media_id) gallery.show(String(variant.featured_media_id), true);
    this.updateCharacter(variant);
  }

  setStickyImage(variant){
    const stickyImage = this.querySelector('.sticky-buy-bar__img');
    if (stickyImage && variant && variant.image) { stickyImage.src = variant.image; stickyImage.removeAttribute('srcset'); }
  }

  /* Pack picker changed (pack size or mix counts). State comes from <bundle-picker>. */
  onBundleChange(state){
    const wasMix = this.inMixPack();
    this.bundleState = state;
    const mix = this.inMixPack();
    hideFormError(this.errorBox);

    /* A pack sets its own quantities; the quantity field and "Buy it now" (single variant) are 1-pack only. */
    const quantityLabel = this.querySelector('.product-buy [data-quantity-label]');
    const quantity = this.querySelector('.product-buy quantity-input');
    if (quantityLabel) quantityLabel.hidden = mix;
    if (quantity) quantity.hidden = mix;

    if (!mix) {
      if (wasMix) this.applyVariant(this.currentVariant(), true);
      return;
    }

    const dynamic = this.querySelector('[data-dynamic-checkout]');
    if (dynamic) dynamic.hidden = true;
    const price = this.querySelector('[data-price]');
    if (price) {
      const current = price.querySelector('[data-price-current]');
      if (current) current.textContent = state.priceText;
      /* Pack with a display discount: original total struck through, as on the pack card. */
      const onSale = Boolean(state.compareText);
      price.classList.toggle('price--on-sale', onSale);
      const compare = price.querySelector('[data-price-compare]');
      const compareLabel = price.querySelector('[data-compare-label]');
      if (compare) { compare.textContent = onSale ? state.compareText : ''; compare.hidden = !onSale; }
      if (compareLabel) compareLabel.hidden = !onSale;
    }
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = state.priceText;
    if (state.lead) this.setStickyImage(state.lead);

    this.querySelectorAll('[data-add-button]').forEach(btn => {
      btn.disabled = !state.complete;
      btn.setAttribute('aria-disabled', state.complete ? 'false' : 'true');
      const text = btn.querySelector('[data-add-label]');
      if (text) text.textContent = state.label;
      btn.dataset.label = state.label;
    });
  }

  /* Style-specific presentation: gallery tint + corner mascot and the "Meet …" line.
     Everything comes from the per-variant data rendered by Liquid (variant metafields,
     then the style option value, then the product) — no style names in this file. */
  updateCharacter(variant){
    const frame = this.querySelector('[data-gallery-frame]');
    if (frame) frame.style.background = `var(--tint-${variant.character || 'default'})`;
    const badge = this.querySelector('[data-gallery-badge]');
    if (badge) {
      badge.innerHTML = variant.mascot || '';
      badge.hidden = !variant.mascot;
    }
    const line = this.querySelector('[data-character-line]');
    if (line) {
      line.innerHTML = variant.character_line || '';
      line.hidden = !variant.character_line;
    }
  }

  updatePrice(variant){
    const price = this.querySelector('[data-price]');
    if (!price) return;
    const current = price.querySelector('[data-price-current]');
    const compare = price.querySelector('[data-price-compare]');
    const compareLabel = price.querySelector('[data-compare-label]');
    if (current) current.textContent = variant.price;
    const onSale = Boolean(variant.compare_at_price);
    price.classList.toggle('price--on-sale', onSale);
    if (compare) { compare.textContent = onSale ? variant.compare_at_price : ''; compare.hidden = !onSale; }
    if (compareLabel) compareLabel.hidden = !onSale;
  }

  setButtonsAvailability(variant){
    const label = !variant ? strings.unavailable : variant.available ? strings.addToCart : strings.soldOut;
    const enabled = Boolean(variant && variant.available);
    this.querySelectorAll('[data-add-button]').forEach(btn => {
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      const text = btn.querySelector('[data-add-label]');
      if (text) text.textContent = label;
      btn.dataset.label = label;
    });
  }
}
customElements.define('product-info', ProductInfo);

/* ==========================================================
   <bundle-picker> — 1 / 2 / 3 packs (snippets/product-bundle-panel.liquid)
   The 1 pack is the normal product form. The 2 and 3 packs mix
   and match styles (repeats allowed); each style row resolves to
   a real variant from the product JSON, and the pack is added
   with one /cart/add.js request by <product-info>. Prices are the
   variants' real prices, summed — no discount is assumed.
========================================================== */

/* Money format of the current currency, read from a Liquid-formatted sample (1234567 → "£12,345.67"). */
function moneyFormatFromSample(sample){
  const match = sample && sample.match(/\d[\d.,'\s ]*\d/);
  if (!match) return theme.moneyFormat;
  const number = match[0];
  const decimals = /[.,]\d{2}$/.test(number) && !/^\d{1,3}([.,'\s ])\d{3}$/.test(number);
  const decimalMark = decimals ? number.slice(-3, -2) : '';
  const grouping = number.slice(0, decimals ? -3 : undefined).replace(/\d/g, '').charAt(0);
  let key = 'amount';
  if (decimals) {
    if (decimalMark === ',') key = /\s| /.test(grouping) ? 'amount_with_space_separator' : 'amount_with_comma_separator';
    else if (grouping === "'") key = 'amount_with_apostrophe_separator';
    else if (/\s| /.test(grouping)) key = 'amount_with_period_and_space_separator';
  } else {
    key = grouping === '.' ? 'amount_no_decimals_with_comma_separator'
      : /\s| /.test(grouping) ? 'amount_no_decimals_with_space_separator' : 'amount_no_decimals';
  }
  return sample.replace(number, `{{${key}}}`);
}

class BundlePicker extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.info = this.closest('product-info');
    const json = this.info && this.info.querySelector('[data-product-json]');
    this.variants = json ? (JSON.parse(json.textContent).variants || []) : [];
    const stringsJson = this.querySelector('[data-bundle-strings]');
    this.text = stringsJson ? JSON.parse(stringsJson.textContent) : {};
    this.moneyFormat = moneyFormatFromSample(this.dataset.moneySample);
    this.styleIndex = Number(this.dataset.stylePosition) - 1;
    this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
    if (!this.info || !this.variants.length || this.styleIndex < 0) return;

    /* Mix counts per pack size: { 2: { Carrot: 0, … }, 3: { … } } */
    this.counts = {};
    this.cards.forEach(card => {
      card.hidden = false;
      const size = Number(card.dataset.pack);
      if (size < 2) return;
      this.counts[size] = {};
      card.querySelectorAll('[data-mix-row]').forEach(row => { this.counts[size][row.dataset.styleValue] = 0; });
    });
    /* The 3 pack starts as one of each available style (as many as fit). */
    if (this.counts[3]) this.fillEvenly(3);

    this.addEventListener('change', event => {
      if (event.target.matches('[data-bundle-input]')) this.update(true);
    });
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-mix-plus], [data-mix-minus]');
      if (!button || button.disabled) return;
      const row = button.closest('[data-mix-row]');
      const size = Number(button.closest('[data-bundle-card]').dataset.pack);
      this.step(size, row.dataset.styleValue, button.hasAttribute('data-mix-plus') ? 1 : -1);
    });
    this.update(true);
  }

  get size(){
    const checked = this.querySelector('[data-bundle-input]:checked');
    return checked ? Number(checked.value) : 1;
  }

  /* The variant for a style value: same other options as the current selection, if any. */
  variantFor(value){
    const current = this.info.currentVariant() || this.variants[0];
    const matches = this.variants.filter(v => v.options[this.styleIndex] === value);
    return matches.find(v => v.options.every((opt, i) => i === this.styleIndex || !current || opt === current.options[i])) || matches[0] || null;
  }

  /* Most of one style a pack may hold: 0 when sold out, else the variant's quantity limit. */
  maxFor(variant){
    if (!variant || !variant.available) return 0;
    return variant.quantity_max ? Number(variant.quantity_max) : Infinity;
  }

  total(size){
    return Object.values(this.counts[size] || {}).reduce((sum, n) => sum + n, 0);
  }

  fillEvenly(size){
    const counts = this.counts[size];
    let added = true;
    while (this.total(size) < size && added) {
      added = false;
      for (const value of Object.keys(counts)) {
        if (this.total(size) >= size) break;
        if (counts[value] < this.maxFor(this.variantFor(value))) { counts[value] += 1; added = true; }
      }
    }
  }

  step(size, value, direction){
    const counts = this.counts[size];
    if (!counts || !(value in counts)) return;
    const variant = this.variantFor(value);
    if (direction > 0) {
      if (this.total(size) >= size || counts[value] >= this.maxFor(variant)) return;
      counts[value] += 1;
      this.dispatchEvent(new CustomEvent('bundle:preview', { bubbles: true, detail: { variant } }));
    } else {
      if (counts[value] <= 0) return;
      counts[value] -= 1;
    }
    this.update(true);
  }

  /* Re-read availability after the variant selection changed, without telling <product-info>. */
  refresh(){
    this.update(false);
  }

  format(cents){
    return formatMoney(cents, this.moneyFormat);
  }

  fill(template, values){
    return Object.keys(values).reduce((text, key) => text.split(`[${key}]`).join(values[key]), template || '');
  }

  /* Display-only discount for a pack: the "Matching automatic discount (%)" block setting, which must
     mirror an automatic discount in Shopify Admin. Shopify takes the percentage off each item,
     rounded down to the penny, so the same rounding is used here. Nothing here discounts the cart. */
  discountedUnit(size, cents){
    const card = this.cards.find(c => Number(c.dataset.pack) === size);
    const percent = card ? Number(card.dataset.packDiscount) || 0 : 0;
    return cents - Math.floor(cents * percent / 100);
  }

  /* Prices for a pack: the real total once complete; otherwise chosen styles plus the lowest available
     price for the rest. `cents` is after the display discount, `original` before it. */
  packPricing(size){
    const available = this.variants.filter(v => v.available);
    const pool = available.length ? available : this.variants;
    const prices = pool.map(v => Number(v.price_cents) || 0);
    const lowest = prices.length ? Math.min(...prices) : 0;
    const varies = new Set(prices).size > 1;
    if (size === 1) {
      const variant = this.info.currentVariant();
      const original = variant ? Number(variant.price_cents) : lowest;
      const cents = this.discountedUnit(1, original);
      return { cents, original, unit: cents, from: false, items: variant ? [{ id: variant.id, quantity: 1 }] : [], lead: variant, complete: Boolean(variant && variant.available) };
    }
    const items = [];
    let cents = 0;
    let original = 0;
    let lead = null;
    const unitPrices = new Set();
    Object.entries(this.counts[size]).forEach(([value, quantity]) => {
      if (!quantity) return;
      const variant = this.variantFor(value);
      if (!variant) return;
      const price = Number(variant.price_cents);
      items.push({ id: variant.id, quantity });
      original += price * quantity;
      cents += this.discountedUnit(size, price) * quantity;
      unitPrices.add(price);
      if (!lead) lead = variant;
    });
    const chosen = this.total(size);
    const complete = chosen === size && items.length > 0;
    if (!complete) {
      original += (size - chosen) * lowest;
      cents += (size - chosen) * this.discountedUnit(size, lowest);
    }
    const uniform = complete ? unitPrices.size === 1 : !varies;
    return { cents, original, unit: uniform ? cents / size : null, from: !complete && varies, items, lead, complete };
  }

  update(notify){
    const selected = this.size;
    this.cards.forEach(card => {
      const size = Number(card.dataset.pack);
      const isSelected = size === selected;
      card.classList.toggle('is-selected', isSelected);
      const body = card.querySelector('[data-bundle-body]');
      if (body) body.hidden = !isSelected;
      if (size > 1) this.renderMix(card, size);

      const pricing = this.packPricing(size);
      const priceText = pricing.from ? this.fill(this.text.from, { price: this.format(pricing.cents) }) : this.format(pricing.cents);
      const priceEl = card.querySelector('[data-bundle-price]');
      if (priceEl) priceEl.textContent = priceText;
      /* Original total struck through, only when a display discount applies (and not for "From" prices). */
      const compareText = pricing.original > pricing.cents && !pricing.from ? this.format(pricing.original) : null;
      pricing.compareText = compareText;
      const compareEl = card.querySelector('[data-bundle-compare]');
      if (compareEl) {
        compareEl.hidden = !compareText;
        const compareValue = compareEl.querySelector('[data-bundle-compare-value]');
        if (compareValue && compareText) compareValue.textContent = compareText;
      }
      const unitEl = card.querySelector('[data-bundle-unit]');
      if (unitEl) {
        unitEl.hidden = pricing.unit === null;
        if (pricing.unit !== null) unitEl.textContent = this.fill(this.text.each, { price: this.format(pricing.unit) });
      }
      if (isSelected) this.state = Object.assign({ size, priceText }, pricing);
    });

    if (!this.state || !notify) return;
    const state = this.state;
    const card = this.cards.find(c => Number(c.dataset.pack) === state.size);
    state.label = state.complete
      ? this.fill(this.text.add, { pack: card ? card.dataset.packTitle : String(state.size) })
      : this.fill(this.text.chooseMore, { remaining: state.size - this.total(state.size) });
    this.dispatchEvent(new CustomEvent('bundle:change', { bubbles: true, detail: state }));
  }

  renderMix(card, size){
    const counts = this.counts[size];
    /* Keep counts valid if availability changed. */
    Object.keys(counts).forEach(value => {
      counts[value] = Math.min(counts[value], this.maxFor(this.variantFor(value)));
    });
    const total = this.total(size);
    card.querySelectorAll('[data-mix-row]').forEach(row => {
      const value = row.dataset.styleValue;
      const max = this.maxFor(this.variantFor(value));
      row.classList.toggle('is-sold-out', max === 0);
      const status = row.querySelector('[data-mix-status]');
      if (status) status.hidden = max !== 0;
      const qty = row.querySelector('[data-mix-qty]');
      if (qty) qty.textContent = counts[value];
      const minus = row.querySelector('[data-mix-minus]');
      const plus = row.querySelector('[data-mix-plus]');
      if (minus) minus.disabled = counts[value] <= 0;
      if (plus) plus.disabled = total >= size || counts[value] >= max;
    });
    const counter = card.querySelector('[data-mix-counter]');
    if (counter) {
      counter.textContent = this.fill(this.text.counter, { selected: this.total(size), size });
      counter.classList.toggle('is-complete', this.total(size) === size);
    }
  }
}
customElements.define('bundle-picker', BundlePicker);

/* ==========================================================
   <sticky-buy-bar> — shown once the product form's Add button scrolls out of view
========================================================== */
class StickyBuyBar extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    const info = this.closest('product-info');
    const target = info && info.querySelector('.product-buy .btn-add');
    this.button = this.querySelector('[data-sticky-add]');
    if (!target || !('IntersectionObserver' in window)) return;
    /* All widths (approved product.html): full-width bar on mobile, floating bar on desktop (CSS). */
    this.observer = new IntersectionObserver(([entry]) => {
      this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    this.observer.observe(target);
  }
  disconnectedCallback(){
    if (this.observer) this.observer.disconnect();
    document.body.classList.remove('has-sticky-buy-bar');
  }
  toggle(show){
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (this.button) this.button.tabIndex = show ? 0 : -1;
    document.body.classList.toggle('has-sticky-buy-bar', show);
  }
}
customElements.define('sticky-buy-bar', StickyBuyBar);

/* ==========================================================
   <facet-filters> — collection + search filtering, sorting,
   density and load more via the Section Rendering API.
   Desktop applies instantly; the mobile sheet applies on "Show".
========================================================== */
class FacetFilters extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.sectionId = this.dataset.section;
    this.formId = this.dataset.form;
    this.drawer = this.querySelector('[data-facets-drawer]');

    this.addEventListener('change', event => {
      const control = event.target;
      if (!control.form || control.form.id !== this.formId) return;
      if (control.matches('[data-desktop-sort]') || isDesktop()) this.apply();
      else this.preview();
    });
    this.addEventListener('input', debounce(event => {
      if (event.target.matches('.facet-price input') && isDesktop()) this.apply();
    }, 600));
    this.addEventListener('submit', event => {
      if (event.target.id !== this.formId) return;
      event.preventDefault();
      this.apply();
    });

    this.addEventListener('click', event => {
      const link = event.target.closest('[data-facet-link], [data-facets-clear]');
      if (link) { event.preventDefault(); this.load(link.href, true); this.closeSheet(); return; }
      if (event.target.closest('[data-facets-open]')) { this.openSheet(event.target.closest('[data-facets-open]')); return; }
      if (event.target.closest('[data-facets-close]')) { this.closeSheet(); return; }
      if (event.target.closest('[data-facets-apply]')) { this.apply(); this.closeSheet(); return; }
      const more = event.target.closest('[data-load-more-button]');
      if (more) { event.preventDefault(); this.loadMore(more); return; }
      const density = event.target.closest('[data-density]');
      if (density) this.setDensity(density.dataset.density);
    });
    this.addEventListener('keydown', event => {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') this.closeSheet();
      else trapFocus(this.drawer, event);
    });

    window.addEventListener('popstate', () => this.load(window.location.href, false));
    let saved = null;
    try { saved = window.localStorage.getItem('pluck:density'); } catch (e) { /* storage blocked */ }
    if (saved) this.setDensity(saved, false);
  }

  params(){
    const form = document.getElementById(this.formId);
    if (!form) return new URLSearchParams();
    const data = new FormData(form);
    /* The desktop <select> and mobile radios both carry sort_by — keep the one in use. */
    const desktopSort = this.querySelector('[data-desktop-sort]');
    const mobileSort = form.querySelector('[data-mobile-sort]:checked');
    data.delete('sort_by');
    const sort = (isDesktop() || !mobileSort) ? (desktopSort && desktopSort.value) : mobileSort.value;
    if (sort) data.append('sort_by', sort);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) { if (value !== '') params.append(key, value); }
    return params;
  }

  url(){
    const query = this.params().toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  apply(){ this.load(this.url(), true); }

  async fetchSection(url){
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}section_id=${this.sectionId}`);
    if (!res.ok) throw new Error(res.status);
    return parseHTML(await res.text());
  }

  /* Mobile sheet: fetch to update the "Show N results" count without touching the grid. */
  async preview(){
    try {
      const doc = await this.fetchSection(this.url());
      const next = doc.querySelector('[data-facets-apply-label]');
      const current = this.querySelector('[data-facets-apply-label]');
      if (next && current) current.textContent = next.textContent;
    } catch (e) { /* the Show button still applies the filters */ }
  }

  async load(url, push){
    const results = this.querySelector('.collection__results');
    if (results) results.classList.add('is-loading');
    try {
      const doc = await this.fetchSection(url);
      const fresh = doc.querySelector('facet-filters');
      if (!fresh) throw new Error('missing section');
      const openIds = new Set(Array.from(this.querySelectorAll('details[open][id]')).map(d => d.id));
      const closedIds = new Set(Array.from(this.querySelectorAll('details:not([open])[id]')).map(d => d.id));
      const focusId = document.activeElement && document.activeElement.id;

      ['.collection__mobile-bar', '.collection__toolbar', '.collection__results', '.facets__form', '.facets__sheet-footer', '.search-tabs-row'].forEach(selector => {
        const current = this.querySelector(selector);
        const next = fresh.querySelector(selector);
        if (current && next) current.innerHTML = next.innerHTML;
      });
      this.querySelectorAll('details[id]').forEach(d => {
        if (openIds.has(d.id)) d.open = true;
        if (closedIds.has(d.id)) d.open = false;
      });
      if (focusId) { const el = document.getElementById(focusId); if (el) el.focus(); }
      if (push) window.history.pushState({}, '', url);
      let saved = null;
      try { saved = window.localStorage.getItem('pluck:density'); } catch (e) { /* storage blocked */ }
      if (saved) this.setDensity(saved, false);
    } catch (error) {
      window.location.href = url;
    } finally {
      const r = this.querySelector('.collection__results');
      if (r) r.classList.remove('is-loading');
    }
  }

  async loadMore(button){
    const grid = this.querySelector('[data-product-grid]');
    if (!grid) { window.location.href = button.href; return; }
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    try {
      const doc = await this.fetchSection(button.href);
      const nextGrid = doc.querySelector('[data-product-grid]');
      const nextMore = doc.querySelector('[data-load-more]');
      const items = nextGrid ? Array.from(nextGrid.children) : [];
      items.forEach(item => grid.appendChild(item));
      const current = this.querySelector('[data-load-more]');
      if (current && nextMore) current.replaceWith(nextMore);
      else if (current) current.remove();
      const firstNew = items[0] && items[0].querySelector('a[href]:not([tabindex="-1"])');
      if (firstNew) firstNew.focus();
    } catch (error) {
      window.location.href = button.href;
    }
  }

  setDensity(columns, save = true){
    const grid = this.querySelector('[data-product-grid]');
    if (grid) grid.style.setProperty('--columns-active', columns);
    this.querySelectorAll('[data-density]').forEach(btn => btn.setAttribute('aria-pressed', btn.dataset.density === String(columns) ? 'true' : 'false'));
    if (save) { try { window.localStorage.setItem('pluck:density', columns); } catch (e) { /* storage blocked */ } }
  }

  openSheet(opener){
    if (!this.drawer) return;
    this.opener = opener;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-modal', 'true');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    lockScroll();
    const panel = this.drawer.querySelector('.facets__panel');
    requestAnimationFrame(() => { const first = panel.querySelector(FOCUSABLE); if (first) first.focus(); });
  }

  closeSheet(){
    if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-modal', 'false');
    unlockScroll();
    if (this.opener) { this.opener.setAttribute('aria-expanded', 'false'); this.opener.focus(); }
  }
}
customElements.define('facet-filters', FacetFilters);

/* ==========================================================
   <product-recommendations> — Shopify Product Recommendations API
========================================================== */
class ProductRecommendations extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    const load = () => this.load();
    if (!('IntersectionObserver' in window)) { load(); return; }
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); load(); }
    }, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }
  async load(){
    try {
      const res = await fetch(this.dataset.url);
      if (!res.ok) throw new Error(res.status);
      const doc = parseHTML(await res.text());
      const grid = doc.querySelector('[data-recommendations-grid]');
      const current = this.querySelector('[data-recommendations-grid]');
      if (grid && grid.children.length && current) current.innerHTML = grid.innerHTML;
      else this.hidden = true;
    } catch (error) {
      this.hidden = true;
    }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ==========================================================
   <featured-slider> — mobile carousel pager dots
========================================================== */
class FeaturedSlider extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.track = this.querySelector('[data-slider-track]');
    this.dots = this.querySelector('[data-slider-dots]');
    if (!this.track || !this.dots) return;
    const items = Array.from(this.track.children);
    this.dots.innerHTML = items.map((_, i) => `<span class="slider-dot${i === 0 ? ' is-active' : ''}"></span>`).join('');
    this.track.addEventListener('scroll', debounce(() => {
      const first = items[0];
      const width = first ? first.getBoundingClientRect().width : 1;
      const index = Math.min(items.length - 1, Math.round(this.track.scrollLeft / width));
      this.dots.querySelectorAll('.slider-dot').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }, 60), { passive: true });
  }
}
customElements.define('featured-slider', FeaturedSlider);

/* ==========================================================
   Theme editor support — open the cart drawer when its section
   is selected, close it when deselected.
========================================================== */
document.addEventListener('shopify:section:select', event => {
  const drawer = event.target.querySelector && event.target.querySelector('cart-drawer');
  if (drawer) drawer.open();
});
document.addEventListener('shopify:section:deselect', event => {
  const drawer = event.target.querySelector && event.target.querySelector('cart-drawer');
  if (drawer) drawer.close();
});

/* ---------- Tap-a-sensor demo (How it works) — synthesized tone, illustrates the interaction only ---------- */
document.addEventListener('DOMContentLoaded', () => {
  const tapBtn = document.getElementById('tapDemoBtn');
  if (!tapBtn) return;
  const notes = [523.25, 587.33, 659.25, 698.46, 783.99];
  let audioCtx;
  tapBtn.addEventListener('click', () => {
    tapBtn.classList.remove('pulsing'); void tapBtn.offsetWidth; tapBtn.classList.add('pulsing');
    try {
      audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioCtx.createOscillator(); const gain = audioCtx.createGain();
      osc.type = 'sine'; osc.frequency.value = notes[Math.floor(Math.random() * notes.length)];
      gain.gain.setValueAtTime(0.0001, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, audioCtx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.4);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(); osc.stop(audioCtx.currentTime + 0.42);
    } catch (err) { /* audio not available */ }
  });
});

/* ---------- Video wall: auto-scrolling, draggable video rail ----------
   [data-video-wall] is the scrolling element; [data-video-wall-track] holds the original cards
   followed by a visual copy of them ([data-video-wall-clone]: aria-hidden, out of the tab order)
   for the seamless loop. Used by the product-page video wall and, below 900px, the homepage video
   teaser (whose copies are hidden on desktop, where the row doesn't scroll). */
function prepareVideoWallCopy(copy){
  copy.dataset.videoWallExtra = 'true';
  copy.classList.remove('is-playing', 'has-started');
  copy.querySelectorAll('video').forEach(video => video.removeAttribute('data-card-video-ready'));
  if (copy.matches('[data-video-card]')) setupCardVideo(copy);
}
function initVideoWall(scrollEl){
  const track = scrollEl.querySelector('[data-video-wall-track]');
  if (!track || scrollEl.dataset.videoWallReady) return;
  scrollEl.dataset.videoWallReady = 'true';
  let autoPaused = false; let resumeTimer = null; let isDragging = false; let dragMoved = false;
  let dragStartX = 0; let dragStartScroll = 0; let suppressNextClick = false;
  const pauseAuto = () => { autoPaused = true; clearTimeout(resumeTimer); };
  const scheduleResume = delay => { clearTimeout(resumeTimer); resumeTimer = setTimeout(() => { autoPaused = false; }, delay); };
  /* Loop distance = from the first original card to the first copy, measured (it includes the gap
     after the last original card, so the wrap lands on an identical frame). The rail must be able
     to scroll at least that far: on wide screens more copies of the card set are appended until it
     can. Re-measured whenever the rail or track resizes (e.g. the homepage copies appear below 900px). */
  let loopDistance = 0;
  const measureLoop = () => {
    const cards = [...track.children].filter(card => card.getClientRects().length);
    const firstCopy = cards.find(card => card.hasAttribute('data-video-wall-clone'));
    loopDistance = firstCopy ? firstCopy.offsetLeft - cards[0].offsetLeft : 0;
    if (loopDistance <= 0) return;
    const copySet = [...track.children].filter(card => card.hasAttribute('data-video-wall-clone') && !card.dataset.videoWallExtra);
    for (let added = 0; scrollEl.scrollWidth - scrollEl.clientWidth < loopDistance + 2 && added < 10; added++) {
      copySet.forEach(card => {
        const copy = card.cloneNode(true);
        prepareVideoWallCopy(copy);
        track.appendChild(copy);
      });
    }
  };
  measureLoop();
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(measureLoop);
    observer.observe(track);
    observer.observe(scrollEl);
  } else {
    window.addEventListener('resize', measureLoop);
  }
  const tick = () => {
    if (!autoPaused && !isDragging && scrollEl.scrollWidth > scrollEl.clientWidth + 1) {
      scrollEl.scrollLeft += 0.6;
      if (loopDistance > 0 && scrollEl.scrollLeft >= loopDistance) scrollEl.scrollLeft -= loopDistance;
    }
    requestAnimationFrame(tick);
  };
  if (!prefersReducedMotion()) requestAnimationFrame(tick);
  scrollEl.addEventListener('mouseenter', pauseAuto);
  scrollEl.addEventListener('mouseleave', () => { if (!isDragging) scheduleResume(300); });
  scrollEl.addEventListener('touchstart', pauseAuto, { passive: true });
  scrollEl.addEventListener('touchend', () => scheduleResume(1500), { passive: true });
  scrollEl.addEventListener('mousedown', e => { isDragging = true; dragMoved = false; dragStartX = e.pageX; dragStartScroll = scrollEl.scrollLeft; scrollEl.classList.add('dragging'); pauseAuto(); });
  window.addEventListener('mousemove', e => { if (!isDragging) return; const dx = e.pageX - dragStartX; if (Math.abs(dx) > 4) dragMoved = true; scrollEl.scrollLeft = dragStartScroll - dx; });
  window.addEventListener('mouseup', () => { if (!isDragging) return; isDragging = false; scrollEl.classList.remove('dragging'); if (dragMoved) suppressNextClick = true; scheduleResume(1200); });
  scrollEl.addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') { scrollEl.scrollLeft += 190; pauseAuto(); scheduleResume(1500); }
    if (e.key === 'ArrowLeft') { scrollEl.scrollLeft -= 190; pauseAuto(); scheduleResume(1500); }
  });
  /* One listener on the track, so cards copied in later are covered too. */
  track.addEventListener('click', event => {
    const card = [...track.children].find(child => child.contains(event.target));
    if (!card) return;
    /* The end of a drag is not a tap: don't toggle a video, follow a link or show the toast. */
    if (suppressNextClick) { suppressNextClick = false; event.preventDefault(); event.stopPropagation(); return; }
    /* Real video: play/pause is handled by "Card videos" below. Links just navigate. */
    if (!card.matches('.video-card:not([data-video-card])')) return;
    const toast = scrollEl.closest('section')?.querySelector('.video-toast');
    if (!toast) return;
    toast.classList.add('show');
    setTimeout(() => toast.classList.remove('show'), 2400);
  });
  /* Keep a playing video in view: the auto-scroll pauses while any wall video plays. */
  scrollEl.addEventListener('play', pauseAuto, true);
  ['pause', 'ended'].forEach(type => scrollEl.addEventListener(type, () => {
    if ([...scrollEl.querySelectorAll('video')].every(v => v.paused)) scheduleResume(1500);
  }, true));
}
document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-video-wall]').forEach(initVideoWall));
document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-video-wall]').forEach(initVideoWall));

/* ---------- Card videos (video teaser + video wall cards with a Shopify-hosted video) ----------
   Each card is one play/pause toggle: a full-card <button> with the centred play icon.
   Tap/click/Enter/Space plays the video inline (playsinline, never autoplays). Videos load muted;
   the tap that starts one unmutes it, so sound only ever follows an explicit user action (if a
   browser still refuses playback with sound, it plays muted instead). The same control pauses it. The icon shows whenever the video isn't playing, the label switches between
   "Play video: …" and "Pause video: …", only one card video plays at a time, and a video that
   ends goes back to its poster. State follows the video's own play/pause/ended events.
   The native controls are only the no-JavaScript fallback; they're removed here. */
function pauseOtherCardVideos(video){
  document.querySelectorAll('[data-video-card] video').forEach(other => { if (other !== video && !other.paused) other.pause(); });
}
function syncCardVideo(card){
  const video = card.querySelector('video');
  const toggle = card.querySelector('[data-video-toggle]');
  const playing = Boolean(video) && !video.paused && !video.ended;
  card.classList.toggle('is-playing', playing);
  if (toggle) toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
}
function setupCardVideo(card){
  const video = card.querySelector('video');
  if (!video || video.dataset.cardVideoReady) return;
  video.dataset.cardVideoReady = 'true';
  video.controls = false;
  video.removeAttribute('controls');
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('tabindex', '-1');
  video.addEventListener('play', () => { pauseOtherCardVideos(video); card.classList.add('has-started'); syncCardVideo(card); });
  video.addEventListener('pause', () => syncCardVideo(card));
  video.addEventListener('ended', () => {
    card.classList.remove('has-started');
    video.load(); /* back to the poster frame (preload stays "none") */
    syncCardVideo(card);
  });
  syncCardVideo(card);
}
function toggleCardVideo(card){
  const video = card.querySelector('video');
  if (!video) return;
  if (video.paused || video.ended) {
    pauseOtherCardVideos(video);
    video.muted = false; /* this runs inside the user's tap/click, so sound is allowed */
    card.classList.add('is-playing', 'has-started'); /* hide the icon immediately */
    const played = video.play();
    if (played && played.catch) played.catch(error => {
      if (error && error.name === 'NotAllowedError' && !video.muted) {
        video.muted = true;
        video.play().catch(() => syncCardVideo(card));
      } else {
        syncCardVideo(card);
      }
    });
  } else {
    video.pause();
  }
}
const initCardVideos = root => root.querySelectorAll('[data-video-card]').forEach(setupCardVideo);
document.addEventListener('DOMContentLoaded', () => initCardVideos(document));
document.addEventListener('shopify:section:load', event => initCardVideos(event.target));
document.addEventListener('click', event => {
  const toggle = event.target.closest('[data-video-toggle]');
  if (!toggle) return;
  event.preventDefault();
  const card = toggle.closest('[data-video-card]');
  setupCardVideo(card);
  toggleCardVideo(card);
});

/* ==========================================================
   <review-wall> — live review wall (sections/product-reviews-wall.liquid)
   Loads the product's reviews from Judge.me's storefront review API a page at a time and deals
   them across the four auto-scrolling rows as Pluck cards. Each row holds its cards twice (the
   copy is aria-hidden, its buttons out of the tab order) so the CSS loop is seamless; the track's
   end padding equals the gap, so -50% is exactly one card set. Review text is inserted as text.
   Long reviews and photos open in a <dialog>. If the API fails before anything is shown, the
   reviews Judge.me pre-renders into the judgeme.widget metafield are used; with nothing to show,
   a short honest message is shown instead of the rows.
========================================================== */
class ReviewWall extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.tracks = [...this.querySelectorAll('[data-review-track]')];
    try { this.strings = JSON.parse(this.querySelector('[data-review-strings]').textContent); } catch (e) { this.strings = {}; }
    this.starHtml = this.querySelector('[data-review-star]')?.innerHTML || '';
    this.statusEl = this.querySelector('[data-review-status]');
    this.moreEl = this.querySelector('[data-review-more]');
    this.countEl = this.querySelector('[data-review-count]');
    this.moreButton = this.querySelector('[data-review-load-more]');
    this.dialog = this.querySelector('[data-review-dialog]');
    this.perPage = Number(this.dataset.perPage) || 24;
    this.total = Number(this.dataset.total) || 0;
    this.page = 0;
    this.shown = 0;
    this.reviews = new Map();
    this.failedPhotos = new Set();
    this.canLoadMore = true;
    /* px per second per row, matching the original 42 / 54 / 47 / 61 s placeholder rows */
    this.speeds = [50, 39, 45, 35];
    try {
      this.dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    } catch (e) {
      this.dateFormat = null;
    }

    this.moreButton?.addEventListener('click', () => this.loadPage());
    this.addEventListener('click', event => {
      const opener = event.target.closest('[data-review-open]');
      if (!opener || !this.contains(opener)) return;
      event.preventDefault();
      this.openDialog(opener.closest('[data-review-id]')?.dataset.reviewId, opener);
    });
    this.querySelector('[data-review-dialog-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
    this.dialog?.addEventListener('close', () => {
      this.classList.remove('is-paused');
      this.lastOpener?.focus({ preventScroll: true });
    });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.refreshLayout());
      this.resizeObserver.observe(this);
    }
    this.loadPage();
  }

  disconnectedCallback(){ this.resizeObserver?.disconnect(); }

  async loadPage(){
    if (this.loading || !this.canLoadMore) return;
    this.loading = true;
    if (this.moreButton) this.moreButton.disabled = true;
    if (!this.shown) this.setStatus(this.strings.loading);
    let reviews = [];
    try {
      const params = new URLSearchParams({
        url: this.dataset.shop,
        shop_domain: this.dataset.shop,
        platform: 'shopify',
        page: String(this.page + 1),
        per_page: String(this.perPage),
        product_id: this.dataset.productId
      });
      const response = await fetch(`${this.dataset.api}?${params}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Judge.me ${response.status}`);
      const data = await response.json();
      reviews = (data.reviews || []).map(review => this.fromApi(review));
      if (Number(data.number_of_reviews)) this.total = Number(data.number_of_reviews);
      this.page += 1;
      const pages = data.pagination && Number(data.pagination.total_pages);
      if ((pages && this.page >= pages) || reviews.length === 0) this.canLoadMore = false;
    } catch (error) {
      if (!this.shown) {
        reviews = this.fromFallback();
        this.canLoadMore = false;
      } else {
        this.setStatus(this.strings.error);
      }
    }
    this.loading = false;
    if (this.moreButton) this.moreButton.disabled = false;
    this.addReviews(reviews);
  }

  fromApi(review){
    return {
      id: String(review.uuid),
      rating: Number(review.rating) || 0,
      /* The API sends these HTML-escaped (e.g. &#39;); decode to plain text (still shown as text) */
      name: this.decode(review.reviewer_name),
      title: this.decode(review.title),
      body: this.paragraphs(review.body_html),
      date: review.created_at,
      verified: review.verified_buyer === true,
      photos: (review.pictures_urls || []).map(picture => ({
        thumb: picture.compact || picture.small || picture.original,
        full: picture.huge || picture.original || picture.compact
      })).filter(picture => picture.thumb)
    };
  }

  fromFallback(){
    const template = this.querySelector('[data-review-fallback]');
    if (!template) return [];
    return [...template.content.querySelectorAll('.jdgm-rev')].map(review => ({
      id: review.dataset.reviewId || String(Math.random()),
      rating: Number(review.querySelector('.jdgm-rev__rating')?.dataset.score) || 0,
      name: review.querySelector('.jdgm-rev__author')?.textContent.trim() || '',
      title: review.querySelector('.jdgm-rev__title')?.textContent.trim() || '',
      body: this.paragraphs(review.querySelector('.jdgm-rev__body')?.innerHTML),
      date: review.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime'),
      verified: review.dataset.verifiedBuyer === 'true',
      photos: [...review.querySelectorAll('.jdgm-rev__pics img')].map(img => ({
        thumb: img.getAttribute('data-src') || img.getAttribute('src'),
        full: img.closest('a')?.getAttribute('href') || img.getAttribute('data-src') || img.getAttribute('src')
      })).filter(picture => picture.thumb)
    }));
  }

  decode(text){
    return text ? new DOMParser().parseFromString(String(text), 'text/html').documentElement.textContent.trim() : '';
  }

  /* Review HTML → plain-text paragraphs (parsed inertly; nothing from it is inserted as HTML). */
  paragraphs(html){
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    doc.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
    let parts = [...doc.body.querySelectorAll('p')].map(p => p.textContent.trim()).filter(Boolean);
    if (!parts.length) parts = [doc.body.textContent.trim()].filter(Boolean);
    return parts;
  }

  /* Each row is as tall as its tallest card, so tall cards (long text, photos) are kept together
     in one row instead of stretching every row: new cards are measured off-screen first, then
     tall ones (over 115% of the shortest card) go to the "tall" row and short ones to the others,
     keeping row lengths balanced so
     every row still loops. */
  addReviews(reviews){
    const fresh = reviews.filter(review => !this.reviews.has(review.id));
    if (!this.measureHost) {
      this.measureHost = document.createElement('div');
      this.measureHost.className = 'review-wall__measure';
      this.measureHost.setAttribute('aria-hidden', 'true');
      this.appendChild(this.measureHost);
    }
    const built = fresh.map(review => {
      this.reviews.set(review.id, review);
      const card = this.buildCard(review, false);
      this.measureHost.appendChild(card);
      return { review, card };
    });
    built.forEach(({ card }) => this.updateClamp(card));
    const heights = built.map(({ card }) => card.getBoundingClientRect().height);
    heights.forEach(height => { if (height > 0) this.shortHeight = Math.min(this.shortHeight || Infinity, height); });
    built.forEach(({ review, card }, index) => {
      const tall = this.shortHeight && heights[index] > this.shortHeight * 1.15;
      const track = this.pickTrack(tall);
      const firstCopy = track.querySelector('[data-review-clone]');
      track.insertBefore(card, firstCopy);
      track.appendChild(this.buildCard(review, true));
      this.shown += 1;
    });
    this.classList.toggle('is-empty', this.shown === 0);
    this.classList.add('is-ready');
    if (this.shown === 0) this.setStatus(this.strings.empty || this.strings.error);
    else if (!this.statusEl.textContent || this.statusEl.textContent === this.strings.loading) this.setStatus('');
    if (this.countEl) this.countEl.textContent = (this.strings.count || '').replace('[shown]', this.shown).replace('[total]', Math.max(this.total, this.shown));
    if (this.moreEl) this.moreEl.hidden = this.shown === 0 || !this.canLoadMore || this.shown >= this.total;
    this.refreshLayout();
  }

  buildCard(review, isCopy){
    const card = document.createElement(isCopy ? 'div' : 'article');
    card.className = 'review-card review-card--live';
    card.dataset.reviewId = review.id;
    if (isCopy) {
      card.dataset.reviewClone = '';
      card.setAttribute('aria-hidden', 'true');
    }
    card.appendChild(this.buildStars(review.rating));
    if (review.title) {
      const title = document.createElement('p');
      title.className = 'review-card__title';
      title.textContent = review.title;
      card.appendChild(title);
    }
    const body = document.createElement('div');
    body.className = 'review-card__body';
    review.body.forEach(text => {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      body.appendChild(paragraph);
    });
    card.appendChild(body);
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'review-card__more';
    more.dataset.reviewOpen = '';
    more.textContent = this.strings.readMore || 'Read more';
    more.hidden = true;
    if (isCopy) more.tabIndex = -1;
    card.appendChild(more);
    if (review.photos.length) {
      /* The first photo is a small thumbnail on the star line (top-right) with a "+N" badge, so
         photo reviews stay close to the height of text-only ones; every photo opens in the dialog. */
      card.classList.add('has-photo');
      const media = document.createElement('div');
      media.className = 'review-card__media';
      const updateCount = () => { media.dataset.more = media.children.length > 1 ? `+${media.children.length - 1}` : ''; };
      review.photos.slice(0, 3).forEach(photo => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'review-card__photo';
        button.dataset.reviewOpen = '';
        if (isCopy) button.tabIndex = -1;
        const img = document.createElement('img');
        img.src = photo.thumb;
        img.alt = (this.strings.photo || '').replace('[name]', review.name);
        img.width = 32;
        img.height = 32;
        img.loading = 'lazy';
        img.decoding = 'async';
        /* A photo the host no longer serves is left out rather than shown as a broken box. */
        img.addEventListener('error', () => {
          this.failedPhotos.add(photo.full);
          button.remove();
          updateCount();
          if (!media.children.length) {
            media.remove();
            card.classList.remove('has-photo');
          }
        });
        button.appendChild(img);
        media.appendChild(button);
      });
      updateCount();
      card.appendChild(media);
    }
    card.appendChild(this.buildMeta(review));
    return card;
  }

  buildStars(rating){
    const stars = document.createElement('div');
    stars.className = 'review-card__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', (this.strings.rated || '').replace('[rating]', rating));
    for (let i = 1; i <= 5; i++) {
      const star = document.createElement('span');
      star.className = `review-card__star${i <= Math.round(rating) ? ' is-filled' : ''}`;
      star.innerHTML = this.starHtml; /* the theme's own star icon (from Liquid), not review data */
      stars.appendChild(star);
    }
    return stars;
  }

  buildMeta(review){
    const meta = document.createElement('p');
    meta.className = 'review-card__meta';
    const name = document.createElement('span');
    name.className = 'review-card__name';
    name.textContent = review.name;
    meta.appendChild(name);
    if (review.verified) {
      const badge = document.createElement('span');
      badge.className = 'review-card__verified';
      badge.textContent = this.strings.verified || '';
      meta.appendChild(badge);
    }
    const date = review.date ? new Date(review.date) : null;
    if (date && !Number.isNaN(date.getTime())) {
      const time = document.createElement('time');
      time.dateTime = date.toISOString().slice(0, 10);
      time.textContent = this.dateFormat ? this.dateFormat.format(date) : time.dateTime;
      meta.appendChild(time);
    }
    return meta;
  }

  refreshLayout(){
    this.tracks.forEach((track, index) => {
      const row = track.parentElement;
      /* One card set = the original cards plus a gap each (the copies may be hidden). */
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const half = [...track.children]
        .filter(card => !card.hasAttribute('data-review-clone'))
        .reduce((sum, card) => sum + card.getBoundingClientRect().width + gap, 0);
      /* Too few cards to fill the row: show them still, without the loop copy. */
      row.classList.toggle('is-static', half > 0 && half < row.clientWidth);
      const speed = this.speeds[index % this.speeds.length];
      if (half > 0) track.style.setProperty('--duration', `${Math.max(20, half / speed).toFixed(1)}s`);
    });
    this.querySelectorAll('[data-review-track] .review-card--live').forEach(card => this.updateClamp(card));
  }

  /* "Read more" only where the clamp actually hides text */
  updateClamp(card){
    const body = card.querySelector('.review-card__body');
    const more = card.querySelector('.review-card__more');
    if (body && more) more.hidden = body.scrollHeight <= body.clientHeight + 1;
  }

  /* Tall cards → always the third row (a longer row just loops over a longer distance at the same
     speed); short cards → the shortest of the other rows, or the tall row while it is behind. */
  pickTrack(tall){
    const counts = this.tracks.map(track => track.querySelectorAll('article').length);
    const tallIndex = Math.min(2, this.tracks.length - 1);
    const others = counts.map((count, index) => index).filter(index => index !== tallIndex);
    if (tall || !others.length) return this.tracks[tallIndex];
    const shortest = others.reduce((best, index) => (counts[index] < counts[best] ? index : best), others[0]);
    return this.tracks[counts[tallIndex] < counts[shortest] ? tallIndex : shortest];
  }

  openDialog(id, opener){
    const review = this.reviews.get(id);
    if (!review || !this.dialog) return;
    this.lastOpener = opener?.closest('[data-review-clone]') ? null : opener;
    const stars = this.dialog.querySelector('[data-review-dialog-stars]');
    stars.replaceChildren(this.buildStars(review.rating));
    const name = this.dialog.querySelector('[data-review-dialog-name]');
    name.replaceChildren(...this.buildMeta(review).childNodes);
    const body = this.dialog.querySelector('[data-review-dialog-body]');
    body.replaceChildren();
    if (review.title) {
      const title = document.createElement('p');
      title.className = 'review-card__title';
      title.textContent = review.title;
      body.appendChild(title);
    }
    review.body.forEach(text => {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      body.appendChild(paragraph);
    });
    const media = this.dialog.querySelector('[data-review-dialog-media]');
    media.replaceChildren(...review.photos.filter(photo => !this.failedPhotos.has(photo.full)).map(photo => {
      const img = document.createElement('img');
      img.src = photo.full;
      img.alt = (this.strings.photo || '').replace('[name]', review.name);
      img.loading = 'lazy';
      img.addEventListener('error', () => img.remove());
      return img;
    }));
    this.classList.add('is-paused');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  setStatus(text){
    if (!this.statusEl) return;
    this.statusEl.textContent = text || '';
    this.statusEl.hidden = !text;
  }
}
customElements.define('review-wall', ReviewWall);

/* ==========================================================
   <hero-teaser> — small silent looping video over the main product image (snippets/product-hero-teaser.liquid)
   The loop plays only while the teaser is on screen, never for reduced-motion visitors, and pauses
   whenever any other video on the page plays (so only one video plays at a time). Tapping it pauses
   every other video and opens a modal <dialog> (focus contained, Escape / backdrop / close button
   close it, page scroll locked) where the full video plays with sound and native controls; on
   close it pauses and focus returns to the teaser.
========================================================== */
class HeroTeaser extends HTMLElement {
  connectedCallback(){
    if (this._ready) return;
    this._ready = true;
    this.card = this.querySelector('.hero-teaser__card');
    this.loop = this.querySelector('[data-hero-teaser-loop]');
    this.dialog = this.querySelector('dialog');
    this.full = this.querySelector('[data-hero-teaser-full]');
    if (!this.card || !this.loop || !this.dialog || !this.full) return;
    this.loop.muted = true;
    this.loop.defaultMuted = true;
    this.reduced = prefersReducedMotion();
    this.visible = false;
    this.otherPlaying = new Set();

    if (!this.reduced && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.updateLoop();
      }, { threshold: 0.25 });
      this.observer.observe(this);
    }

    this.card.addEventListener('click', () => this.open());
    this.querySelector('[data-hero-teaser-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
    this.dialog.addEventListener('close', () => this.onClose());

    /* Any other video starting pauses the loop; it resumes once none are playing. */
    this.onPlay = event => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement) || video === this.loop) return;
      this.otherPlaying.add(video);
      this.updateLoop();
    };
    this.onStop = event => {
      if (this.otherPlaying.delete(event.target)) this.updateLoop();
    };
    this.onVisibility = () => this.updateLoop();
    document.addEventListener('play', this.onPlay, true);
    document.addEventListener('pause', this.onStop, true);
    document.addEventListener('ended', this.onStop, true);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback(){
    this.observer?.disconnect();
    document.removeEventListener('play', this.onPlay, true);
    document.removeEventListener('pause', this.onStop, true);
    document.removeEventListener('ended', this.onStop, true);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  updateLoop(){
    const shouldPlay = !this.reduced && this.visible && !this.dialog.open && this.otherPlaying.size === 0 && !document.hidden;
    if (shouldPlay && this.loop.paused) {
      const played = this.loop.play();
      if (played && played.catch) played.catch(() => {}); /* autoplay refused: the poster stays */
    } else if (!shouldPlay && !this.loop.paused) {
      this.loop.pause();
    }
  }

  open(){
    document.querySelectorAll('video').forEach(video => { if (video !== this.full && !video.paused) video.pause(); });
    const frame = this.closest('[data-gallery-frame]') || this.parentElement;
    this.dialog.style.setProperty('--teaser-frame', `${Math.round(frame.getBoundingClientRect().width)}px`);
    document.documentElement.classList.add('has-hero-teaser-open');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.updateLoop();
    /* The tap is an explicit request to watch, so the full video may play with sound. */
    this.full.muted = false;
    const played = this.full.play();
    if (played && played.catch) played.catch(error => {
      if (error && error.name === 'NotAllowedError') {
        this.full.muted = true;
        this.full.play().catch(() => {});
      }
    });
  }

  onClose(){
    this.full.pause();
    document.documentElement.classList.remove('has-hero-teaser-open');
    this.card.focus({ preventScroll: true });
    this.updateLoop();
  }
}
customElements.define('hero-teaser', HeroTeaser);

